/**
 * Medication Sort Utilities
 *
 * Provides a deterministic, stable sort comparator for user medications.
 *
 * Sort order:
 *   1. Active status — active (no endDate) first, stopped second
 *   2. Start date — most recently started first (descending)
 *   3. Display name — alphabetical, case-insensitive (ascending)
 *
 * The `showInTrends` flag intentionally does NOT affect ordering.
 * This prevents the list from rearranging when the user toggles that setting.
 */

/** Minimal medication shape required for sorting */
export interface SortableMedication {
  /** Stable identifier (used as final deterministic tiebreaker when present) */
  id?: string;
  /** When the user started taking this medication (YYYY-MM-DD) */
  startDate: string;
  /** When the user stopped (null = still active) */
  endDate: string | null;
  /** User-facing medication name */
  displayName: string;
}

/**
 * Deterministic comparator for sorting medications.
 *
 * Ordering keys (in priority order):
 *   1. Active status: active (endDate === null) before stopped
 *   2. Start date: most recently started first (descending)
 *   3. Display name: alphabetical, case-insensitive (ascending)
 *
 * @returns negative if `a` should come first, positive if `b` should come first, 0 if equal
 */
export function compareMedications<T extends SortableMedication>(
  a: T,
  b: T,
): number {
  // 1. Active status: active first (endDate === null)
  const aActive = a.endDate === null ? 0 : 1;
  const bActive = b.endDate === null ? 0 : 1;
  if (aActive !== bActive) return aActive - bActive;

  // 2. Start date: most recently started first (descending)
  // Compare as strings since they're YYYY-MM-DD format (lexicographic = chronological)
  if (a.startDate > b.startDate) return -1;
  if (a.startDate < b.startDate) return 1;

  // 3. Display name: alphabetical, case-insensitive (ascending)
  const byName = a.displayName
    .toLowerCase()
    .localeCompare(b.displayName.toLowerCase());

  if (byName !== 0) return byName;

  // 4. Deterministic tiebreaker: id (when available)
  // This avoids relying on engine sort stability or server return order when
  // the primary sort keys are identical.
  if (a.id && b.id) return a.id.localeCompare(b.id);

  return 0;
}

/**
 * Sort an array of medications using the deterministic comparator.
 * Returns a new sorted array (does not mutate the input).
 */
export function sortMedications<T extends SortableMedication>(
  medications: T[],
): T[] {
  return [...medications].sort(compareMedications);
}
